import { z } from 'zod';
export const workforceHrFormDefinitionsCapability = 'workforce.hr-form-definitions.v1';
export const workforceDocumentsProtectedFormEligibilityCapability = 'workforce.documents-protected-form-eligibility.v1';
export const workforceDocumentsProtectedFormSubmissionCapability = 'workforce.documents-protected-form-submission.v1';
export const workforceDocumentsProtectedFormEligibilitySlot = 'documents.workforce-protected-form-eligibility';
export const workforceDocumentsProtectedFormSubmissionSlot = 'documents.workforce-protected-form-submissions';
export const workforceDocumentsProtectedFormEligibilityProviderId = 'workforce.server.documents-protected-form-eligibility';
export const workforceDocumentsProtectedFormSubmissionProviderId = 'workforce.server.documents-protected-form-submission';
const uuidSchema = z.string().uuid();
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const reasonSchema = z.string().trim().min(10).max(500);
const keySchema = z.string().trim().min(1).max(100).regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/, 'Workforce HR form keys use lower-case letters, numbers, and hyphens.');
export const workforceHrFormPurposeSchema = z.enum([
    'onboarding',
    'tax-withholding',
    'employment-eligibility',
    'payroll-banking',
    'incident',
    'performance-review',
    'corrective-action',
    'offboarding',
    'other-personnel',
]);
export const workforceHrFormSensitivitySchema = z.enum([
    'confidential',
    'tax',
    'employment-eligibility',
    'banking',
    'medical',
    'personnel-restricted',
]);
export const workforceHrFormAudienceSchema = z.enum([
    'self-service',
    'manager-assigned',
    'self-or-manager',
]);
export const workforceHrFormFieldTypeSchema = z.enum([
    'text', 'multiline', 'date', 'number', 'currency', 'boolean', 'choice', 'multi-choice', 'file',
]);
export const workforceHrFormStatusSchema = z.enum(['draft', 'published', 'archived']);
export const workforceHrFormFieldDefinitionSchema = z.strictObject({
    key: z.string().trim().min(1).max(120).regex(/^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/),
    label: z.string().trim().min(1).max(200),
    type: workforceHrFormFieldTypeSchema,
    required: z.boolean(),
    sensitive: z.boolean(),
    options: z.array(z.string().trim().min(1).max(160)).max(100).default([]),
}).superRefine((field, context) => {
    const choice = field.type === 'choice' || field.type === 'multi-choice';
    if (choice !== (field.options.length > 0))
        context.addIssue({
            code: 'custom', path: ['options'], message: 'Only choice fields require nonempty options.',
        });
    if (new Set(field.options.map((option) => option.toLocaleLowerCase('en-US'))).size !==
        field.options.length)
        context.addIssue({
            code: 'custom', path: ['options'], message: 'Workforce HR form options must be unique.',
        });
});
export const workforceHrFormDefinitionContentSchema = z.strictObject({
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2_000).nullable(),
    purpose: workforceHrFormPurposeSchema,
    sensitivity: workforceHrFormSensitivitySchema,
    audience: workforceHrFormAudienceSchema,
    documentsAssetId: uuidSchema,
    documentsAssetVersionId: uuidSchema,
    documentsAssetSha256: sha256Schema,
    fields: z.array(workforceHrFormFieldDefinitionSchema).max(100),
    completedDocumentRequired: z.literal(true),
    signatureRequired: z.literal(true),
    initialsRequired: z.literal(true),
    consentText: z.string().trim().min(1).max(4_000),
    maximumAttachments: z.number().int().min(0).max(10),
    allowedAttachmentContentTypes: z.array(z.enum([
        'application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'image/heif',
    ])).max(5),
    notificationPolicy: z.strictObject({
        notificationOnly: z.literal(true),
        emailAttachmentsAllowed: z.literal(false),
    }),
}).superRefine((content, context) => {
    const keys = content.fields.map(({ key }) => key);
    if (new Set(keys).size !== keys.length)
        context.addIssue({
            code: 'custom', path: ['fields'], message: 'Workforce HR form field keys must be unique.',
        });
    if (content.maximumAttachments === 0 && content.allowedAttachmentContentTypes.length > 0) {
        context.addIssue({
            code: 'custom', path: ['allowedAttachmentContentTypes'],
            message: 'A form that allows no attachments cannot declare attachment content types.',
        });
    }
    if (content.maximumAttachments > 0 && content.allowedAttachmentContentTypes.length === 0) {
        context.addIssue({
            code: 'custom', path: ['allowedAttachmentContentTypes'],
            message: 'A form that allows attachments must declare allowed content types.',
        });
    }
});
export const workforceHrFormDefinitionVersionSchema = z.strictObject({
    id: uuidSchema,
    tenantId: uuidSchema,
    definitionId: uuidSchema,
    versionNumber: z.number().int().positive(),
    sourceVersionId: uuidSchema.nullable(),
    content: workforceHrFormDefinitionContentSchema,
    contentHash: sha256Schema,
    reason: reasonSchema,
    createdByMembershipId: uuidSchema,
    createdAt: z.string().datetime({ offset: true }),
});
export const workforceHrFormDefinitionSchema = z.strictObject({
    id: uuidSchema,
    tenantId: uuidSchema,
    key: keySchema,
    status: workforceHrFormStatusSchema,
    publishedVersionId: uuidSchema.nullable(),
    latestVersionId: uuidSchema,
    revision: z.number().int().positive(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: uuidSchema,
    updatedByMembershipId: uuidSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    publishedVersion: workforceHrFormDefinitionVersionSchema.nullable(),
    latestVersion: workforceHrFormDefinitionVersionSchema,
});
export const workforceHrFormDefinitionListQuerySchema = z.strictObject({
    includeArchived: z.boolean().default(false),
    purpose: workforceHrFormPurposeSchema.nullable().default(null),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const workforceCreateHrFormDefinitionSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: uuidSchema,
    definitionId: uuidSchema,
    key: keySchema,
    content: workforceHrFormDefinitionContentSchema,
    reason: reasonSchema,
});
export const workforceAppendHrFormDefinitionVersionSchema = z.strictObject({
    kind: z.literal('append-version'),
    commandId: uuidSchema,
    expectedRevision: z.number().int().positive(),
    content: workforceHrFormDefinitionContentSchema,
    reason: reasonSchema,
});
export const workforcePublishHrFormDefinitionSchema = z.strictObject({
    kind: z.literal('publish'),
    commandId: uuidSchema,
    expectedRevision: z.number().int().positive(),
    versionId: uuidSchema,
    reason: reasonSchema,
});
export const workforceHrFormDefinitionStateCommandSchema = z.strictObject({
    kind: z.enum(['archive', 'restore']),
    commandId: uuidSchema,
    expectedRevision: z.number().int().positive(),
    reason: reasonSchema,
});
export const workforceRollbackHrFormDefinitionSchema = z.strictObject({
    kind: z.literal('rollback'),
    commandId: uuidSchema,
    expectedRevision: z.number().int().positive(),
    sourceVersionId: uuidSchema,
    reason: reasonSchema,
});
export const workforceExistingHrFormDefinitionCommandSchema = z.discriminatedUnion('kind', [
    workforceAppendHrFormDefinitionVersionSchema,
    workforcePublishHrFormDefinitionSchema,
    workforceHrFormDefinitionStateCommandSchema,
    workforceRollbackHrFormDefinitionSchema,
]);
export const workforceHrFormDefinitionResponseSchema = z.strictObject({
    data: workforceHrFormDefinitionSchema,
    command: z.strictObject({ commandId: uuidSchema, idempotent: z.boolean() }).optional(),
});
export const workforceHrFormDefinitionsResponseSchema = z.strictObject({
    data: z.array(workforceHrFormDefinitionSchema),
    page: z.strictObject({
        limit: z.number().int().positive(), offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(), total: z.number().int().nonnegative(),
    }),
});
export const workforceHrFormDefinitionVersionsResponseSchema = z.strictObject({
    data: z.array(workforceHrFormDefinitionVersionSchema),
});
export const workforceDocumentsProtectedFormEligibilityRequestSchema = z.strictObject({
    consumerModuleId: z.literal('documents'),
    requestedCapability: z.literal('documents.workforce-protected-forms.v1'),
    resourceType: z.literal('documents.workforce-protected-form-submission'),
    resourceId: uuidSchema,
    definitionId: uuidSchema,
    expectedDefinitionVersionId: uuidSchema,
    targetMembershipId: uuidSchema,
    expectedDocumentsAssetId: uuidSchema,
    expectedDocumentsAssetVersionId: uuidSchema,
});
export const workforceDocumentsProtectedFormEligibilityResponseSchema = z.strictObject({
    state: z.literal('eligible'),
    capability: z.literal(workforceDocumentsProtectedFormEligibilityCapability),
    tenantId: uuidSchema,
    actorMembershipId: uuidSchema,
    targetMembershipId: uuidSchema,
    definitionId: uuidSchema,
    definitionVersionId: uuidSchema,
    definitionVersionNumber: z.number().int().positive(),
    definitionContentHash: sha256Schema,
    definition: workforceHrFormDefinitionContentSchema,
    factHash: sha256Schema,
});
export const workforceDocumentsProtectedFormSubmissionRequestSchema = z.strictObject({
    consumerModuleId: z.literal('documents'),
    requestedCapability: z.literal(workforceDocumentsProtectedFormSubmissionCapability),
    sourceCapability: z.literal('documents.workforce-protected-forms.v1'),
    commandId: uuidSchema,
    definitionId: uuidSchema,
    definitionVersionId: uuidSchema,
    targetMembershipId: uuidSchema,
    documentsSubmissionId: uuidSchema,
    documentsAssetId: uuidSchema,
    documentsAssetVersionId: uuidSchema,
    documentsAssetSha256: sha256Schema,
    submissionProofHash: sha256Schema,
    submittedAt: z.string().datetime({ offset: true }),
});
export const workforceDocumentsProtectedFormSubmissionResponseSchema = z.strictObject({
    state: z.literal('recorded'),
    capability: z.literal(workforceDocumentsProtectedFormSubmissionCapability),
    tenantId: uuidSchema,
    receiptId: uuidSchema,
    definitionId: uuidSchema,
    definitionVersionId: uuidSchema,
    targetMembershipId: uuidSchema,
    documentsSubmissionId: uuidSchema,
    submissionProofHash: sha256Schema,
    recordedAt: z.string().datetime({ offset: true }),
    idempotent: z.boolean(),
});
export const workforceProtectedFormSubmissionReceiptSchema = z.strictObject({
    id: uuidSchema,
    tenantId: uuidSchema,
    definitionId: uuidSchema,
    definitionVersionId: uuidSchema,
    targetMembershipId: uuidSchema,
    documentsSubmissionId: uuidSchema,
    documentsAssetId: uuidSchema,
    documentsAssetVersionId: uuidSchema,
    documentsAssetSha256: sha256Schema,
    submissionProofHash: sha256Schema,
    submittedAt: z.string().datetime({ offset: true }),
    recordedByMembershipId: uuidSchema,
    recordedAt: z.string().datetime({ offset: true }),
});
export const workforceProtectedFormSubmissionReceiptListQuerySchema = z.strictObject({
    targetMembershipId: uuidSchema.nullable().default(null),
    definitionId: uuidSchema.nullable().default(null),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const workforceProtectedFormSubmissionReceiptsResponseSchema = z.strictObject({
    data: z.array(workforceProtectedFormSubmissionReceiptSchema),
    page: z.strictObject({
        limit: z.number().int().positive(), offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(), total: z.number().int().nonnegative(),
    }),
});
//# sourceMappingURL=workforce-documents-protected-form.contract.js.map